import React, { RefObject } from 'react';
import ReactDOM from 'react-dom';
import styled, { css, StyleSheetManager } from 'styled-components';

import { InputHandler } from '../../../../../viewer/input-handler';
import { reset } from '../../../../utils/styled-reset';
import { DialogPortalContext } from '../../../dialog/dialog';
import { dialogZIndex } from '../../../dialog/dialog-dock';
import {
  getWindowBounds,
  openOwnWindow,
  OwnWindowContext,
  WindowBounds,
} from '../../../dialog/own-window';

/**
 * Opens the right menu in a window of its own, and puts it back, see useRightMenuWindow. For the
 * header of the menu, see RightPanelHeader.
 */
export const RightMenuWindowContext = React.createContext<
  { inWindow: boolean; popOut: () => void; putBack: () => void } | undefined
>(undefined);

/**
 * Opens the menu of the panel in a window of its own, from an icon in its header, which can be
 * moved outside the browser window, see openOwnWindow. The panel is closed meanwhile.
 *
 * The menu stays in a window until it is put back in the panel, from an icon in its header: the
 * window is closed with the menu, e.g. as the tool is ended, also from the close button of the
 * window, and the menu of the next tool opens in a window again, where the last one was, as large
 * as it was. Where the browser doesn't open it then, e.g. as a tool is used without a click, that
 * menu is in the panel. So is the menu of a tool that doesn't end as its window is closed, e.g. as
 * it asks whether to save first, which can be cancelled.
 *
 * Takes the tool whose menu is shown, undefined without a menu. Returns whether the menu is in a
 * window, or opening there, for the panel to be closed, and renders the menu into an element that
 * is moved between the panel and the window, so that it keeps its state.
 */
export const useRightMenuWindow = (menuTool: unknown, panel: RefObject<HTMLElement>) => {
  const hasMenu = menuTool !== undefined;
  const [host] = React.useState(() => document.createElement('div'));
  const [ownWindow, setOwnWindow] = React.useState<Window>();
  const ownWindowRef = React.useRef<Window>();
  ownWindowRef.current = ownWindow;
  const [floatLayer, setFloatLayer] = React.useState<HTMLElement | null>(null);
  // Whether the menu is in a window, until it is put back, see above.
  const [poppedOut, setPoppedOut] = React.useState(false);
  const [opening, setOpening] = React.useState(false);
  // Whether the menu of the tool is in the panel, though popped out: the browser didn't open its
  // window, or the tool didn't end as its window was closed, see endTool.
  const [inPanel, setInPanel] = React.useState(false);
  // Whether the close button of the window was clicked, and the tool is ending, see onClosed.
  const [ending, setEnding] = React.useState(false);
  // Where the last window was, and how large it was.
  const lastBounds = React.useRef<WindowBounds>();
  // Where the menu was as the first window opened, whose size every window asks for, so that the
  // browser opens a Picture-in-Picture window where the last one was, see openOwnWindow.
  const firstRect = React.useRef<DOMRect>();
  // Whether the window is closed by the menu, rather than by its close button.
  const closing = React.useRef(false);
  // The tool of the last menu, see below.
  const lastMenuTool = React.useRef(menuTool);
  // Whether the menu is gone, for a window that opens after it to be closed.
  const unmounted = React.useRef(false);

  React.useLayoutEffect(() => {
    (ownWindow?.document.body ?? panel.current)?.append(host);
  }, [ownWindow, panel, host]);

  React.useLayoutEffect(() => () => host.remove(), [host]);

  // The close button of the window ends the tool, whose menu is closed, rather than opened again.
  // Where the tool doesn't end, e.g. as it asks whether to save first, its menu is put back in the
  // panel, or it would be nowhere where that is cancelled.
  const endTool = React.useCallback(() => {
    const tool = InputHandler.instance.getToolName();
    InputHandler.instance.setTool('select');
    if (InputHandler.instance.getToolName() === tool) {
      setInPanel(true);
    } else {
      setEnding(true);
    }
  }, []);

  const open = React.useCallback(() => {
    if (!panel.current) {
      return;
    }

    setOpening(true);
    firstRect.current ??= panel.current.getBoundingClientRect();
    openOwnWindow(
      firstRect.current,
      document.title,
      (opened) => {
        if (unmounted.current) {
          opened.close();
          return;
        }
        closing.current = false;
        setOpening(false);
        setOwnWindow(opened);
      },
      {
        last: lastBounds.current,
        failed: () => {
          setOpening(false);
          setInPanel(true);
        },
        // Before it has loaded, as its close button does, see onClosed.
        closed: () => {
          if (unmounted.current) {
            return;
          }
          setOpening(false);
          endTool();
        },
      },
    );
  }, [panel, endTool]);

  const close = React.useCallback(() => {
    const current = ownWindowRef.current;
    if (current) {
      lastBounds.current = getWindowBounds(current);
      closing.current = true;
      current.close();
    }
    setOwnWindow(undefined);
  }, []);

  React.useEffect(() => {
    if (!ownWindow) {
      return;
    }

    const onClosed = () => {
      setOwnWindow(undefined);
      if (!closing.current) {
        lastBounds.current = getWindowBounds(ownWindow);
        endTool();
      }
    };
    const closeWithPage = () => {
      closing.current = true;
      ownWindow.close();
    };
    ownWindow.addEventListener('pagehide', onClosed);
    window.addEventListener('pagehide', closeWithPage);

    return () => {
      ownWindow.removeEventListener('pagehide', onClosed);
      window.removeEventListener('pagehide', closeWithPage);
    };
  }, [ownWindow, endTool]);

  // Closed with the menu, and opened again with the next one. Also where a tool is switched to from
  // another, with no menu in between: the menu of the last one may be in the panel.
  React.useEffect(() => {
    const switched = lastMenuTool.current !== menuTool;
    lastMenuTool.current = menuTool;
    if (!hasMenu) {
      close();
      setInPanel(false);
      setEnding(false);
    } else if (switched && inPanel) {
      setInPanel(false);
    } else if (poppedOut && !ownWindow && !opening && !inPanel && !ending) {
      open();
    }
  }, [menuTool, hasMenu, poppedOut, ownWindow, opening, inPanel, ending, open, close]);

  React.useEffect(() => {
    unmounted.current = false;
    return () => {
      unmounted.current = true;
      ownWindowRef.current?.close();
    };
  }, []);

  const popOut = () => {
    setPoppedOut(true);
    setInPanel(false);
    // Right away, while the click lets it be opened.
    open();
  };

  const putBack = () => {
    setPoppedOut(false);
    close();
  };

  const render = (menu: React.ReactNode) =>
    ReactDOM.createPortal(
      // Its styles are added to the window it is in.
      <StyleSheetManager target={ownWindow?.document.head}>
        <OwnWindowContext.Provider value={ownWindow}>
          <RightMenuWindowContext.Provider value={{ inWindow: !!ownWindow, popOut, putBack }}>
            {/* In the window, its dropdowns etc. are shown there. */}
            <DialogPortalContext.Provider value={ownWindow ? floatLayer : null}>
              <Menu $inWindow={!!ownWindow}>{menu}</Menu>
              {ownWindow && <FloatLayer ref={setFloatLayer} />}
            </DialogPortalContext.Provider>
          </RightMenuWindowContext.Provider>
        </OwnWindowContext.Provider>
      </StyleSheetManager>,
      host,
    );

  return { inWindow: poppedOut && !inPanel, ownWindow, render };
};

const Menu = styled.div<{ $inWindow: boolean }>`
  ${reset}
  font-size: 14px;
  height: 100%;
  background-color: white;
  // todo: Bad UX - remove and let user select text. Apply this where necessary.
  user-select: none;

  ${({ $inWindow }) =>
    $inWindow &&
    css`
      height: 100vh;
      overflow-y: auto;
    `}

  > * {
    height: 100%;
    width: 100%;
  }
`;

/**
 * A layer over the window for the dropdowns etc. of the menu, as a dialog has, see FloatLayer in
 * dialog.tsx.
 */
const FloatLayer = styled.div`
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: ${dialogZIndex + 1};
`;
